/**
 * IQP / SS certificate management for a worker ?? add, bulk add, edit grade/expiry, delete.
 * Used from Staff page and legacy worker edit form.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Award, Plus, Pencil, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import {
  IQP_GRADE_OPTIONS,
  groupSsCodesForPicker,
  normalizeSsCode,
  toDateInputValue,
} from '../utils/iqpUtils';

export default function WorkerIqpPanel({ workerId, compact = false }) {
  const queryClient = useQueryClient();
  const [iqpList, setIqpList] = useState([]);
  const [showAddIqp, setShowAddIqp] = useState(false);
  const [showBulkSelect, setShowBulkSelect] = useState(false);
  const [selectedSsCodes, setSelectedSsCodes] = useState([]);
  const [bulkGrade, setBulkGrade] = useState('Registered');
  const [newIqp, setNewIqp] = useState({ ss_code: '', grade: 'Registered', expiry_date: '' });
  const [editingIqpCode, setEditingIqpCode] = useState(null);
  const [editIqpDraft, setEditIqpDraft] = useState({ grade: '', expiry_date: '' });

  const { data: workerData, isLoading: workerLoading } = useQuery({
    queryKey: ['worker', workerId],
    queryFn: async ({ signal }) => {
      const response = await api.get(`/workers/${workerId}`, { signal });
      return response.data;
    },
    enabled: !!workerId,
  });

  useEffect(() => {
    if (workerData?.iqp) setIqpList(workerData.iqp);
  }, [workerData]);

  const {
    data: ssCodesData,
    isLoading: ssCodesLoading,
    error: ssCodesError,
  } = useQuery({
    queryKey: ['ss-codes'],
    queryFn: async ({ signal }) => {
      const response = await api.get('/workers/ss-codes', { signal });
      return response.data?.data || [];
    },
    staleTime: 60_000,
  });

  const groupedSsCodes = useMemo(
    () => groupSsCodesForPicker(ssCodesData || []),
    [ssCodesData]
  );

  const invalidateWorker = () => {
    queryClient.invalidateQueries({ queryKey: ['worker', workerId] });
    queryClient.invalidateQueries({ queryKey: ['workers'] });
    queryClient.invalidateQueries({ queryKey: ['people'] });
  };

  const addIqpMutation = useMutation({
    mutationFn: (iqpData) => api.post(`/workers/${workerId}/iqp`, iqpData).then((r) => r.data),
    onSuccess: (newIqpData) => {
      toast.success('IQP added');
      if (newIqpData) {
        setIqpList((prev) => {
          const code = normalizeSsCode(newIqpData.ss_code);
          return [...prev.filter((iqp) => normalizeSsCode(iqp.ss_code) !== code), { ...newIqpData, ss_code: code }];
        });
      }
      invalidateWorker();
      setShowAddIqp(false);
      setNewIqp({ ss_code: '', grade: '', expiry_date: '' });
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message || 'Failed to add IQP'),
  });

  const updateIqpMutation = useMutation({
    mutationFn: (iqpData) => api.post(`/workers/${workerId}/iqp`, iqpData).then((r) => r.data),
    onSuccess: (updated) => {
      toast.success('IQP updated');
      if (updated) {
        setIqpList((prev) => {
          const code = normalizeSsCode(updated.ss_code);
          return [...prev.filter((iqp) => normalizeSsCode(iqp.ss_code) !== code), { ...updated, ss_code: code }];
        });
      }
      setEditingIqpCode(null);
      setEditIqpDraft({ grade: '', expiry_date: '' });
      invalidateWorker();
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message || 'Failed to update IQP'),
  });

  const deleteIqpMutation = useMutation({
    mutationFn: (ssCode) =>
      api.delete(`/workers/${workerId}/iqp/${encodeURIComponent(ssCode)}`),
    onSuccess: async (_data, ssCode) => {
      toast.success('IQP removed');
      if (editingIqpCode === normalizeSsCode(ssCode)) {
        setEditingIqpCode(null);
        setEditIqpDraft({ grade: '', expiry_date: '' });
      }
      invalidateWorker();
      try {
        const response = await api.get(`/workers/${workerId}`);
        if (response.data?.iqp) setIqpList(response.data.iqp);
        else {
          setIqpList((prev) =>
            prev.filter((iqp) => normalizeSsCode(iqp.ss_code) !== normalizeSsCode(ssCode))
          );
        }
      } catch {
        setIqpList((prev) =>
          prev.filter((iqp) => normalizeSsCode(iqp.ss_code) !== normalizeSsCode(ssCode))
        );
      }
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to delete IQP'),
  });

  const bulkAddMutation = useMutation({
    mutationFn: async () => {
      if (selectedSsCodes.length === 0) throw new Error('Select at least one SS code');
      if (!bulkGrade) throw new Error('Please select a grade');
      const grade = bulkGrade.trim();
      const results = [];
      for (const rawCode of selectedSsCodes) {
        const ssCode = String(rawCode).trim();
        if (!ssCode) continue;
        try {
          const response = await api.post(`/workers/${workerId}/iqp`, {
            ss_code: ssCode,
            grade,
            expiry_date: null,
          });
          results.push({ ssCode, success: true, data: response.data });
        } catch (error) {
          results.push({
            ssCode,
            success: false,
            error: error.response?.data?.error || error.message,
          });
        }
      }
      return results;
    },
    onSuccess: async (results) => {
      const successCount = results.filter((r) => r.success).length;
      const failCount = results.filter((r) => !r.success).length;
      if (successCount > 0) toast.success(`${successCount} SS code(s) added`);
      if (failCount > 0) {
        toast.error(
          `${failCount} failed: ${results.filter((r) => !r.success).map((r) => r.ssCode).join(', ')}`
        );
      }
      try {
        const response = await api.get(`/workers/${workerId}`);
        if (response.data?.iqp) setIqpList(response.data.iqp);
      } catch (e) {
        console.error(e);
      }
      invalidateWorker();
      setShowBulkSelect(false);
      setSelectedSsCodes([]);
      setBulkGrade('Registered');
    },
    onError: (e) => toast.error(e.message || 'Bulk add failed'),
  });

  if (!workerId) {
    return <p className="text-sm text-gray-500">Save the worker first to manage IQP certificates.</p>;
  }

  if (workerLoading) {
    return <p className="text-sm text-gray-500">Loading IQP data…</p>;
  }

  const handleBulkSelect = (ssCode) => {
    setSelectedSsCodes((prev) =>
      prev.includes(ssCode) ? prev.filter((c) => c !== ssCode) : [...prev, ssCode]
    );
  };

  const handleAddIqp = () => {
    const ssCode = (newIqp.ss_code || '').trim();
    const grade = (newIqp.grade || '').trim();
    if (!ssCode || !grade) {
      toast.error('SS code and grade are required');
      return;
    }
    addIqpMutation.mutate({
      ss_code: ssCode,
      grade,
      expiry_date: (newIqp.expiry_date || '').trim() || null,
    });
  };

  const startEditIqp = (iqp) => {
    setShowAddIqp(false);
    setShowBulkSelect(false);
    setEditingIqpCode(normalizeSsCode(iqp.ss_code));
    setEditIqpDraft({
      grade: iqp.grade || '',
      expiry_date: toDateInputValue(iqp.expiry_date),
    });
  };

  const saveEditIqp = (ssCode) => {
    const grade = (editIqpDraft.grade || '').trim();
    if (!grade) {
      toast.error('Grade is required');
      return;
    }
    updateIqpMutation.mutate({
      ss_code: ssCode,
      grade,
      expiry_date: (editIqpDraft.expiry_date || '').trim() || null,
    });
  };

  const titleClass = compact ? 'text-base font-semibold' : 'text-lg font-semibold';

  return (
    <div className={compact ? 'space-y-3' : 'mt-2 space-y-4'}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className={`${titleClass} text-gray-900`}>SS Code / IQP Grade</h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setShowBulkSelect(true);
              setShowAddIqp(false);
              setBulkGrade((g) => g || 'Registered');
            }}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Plus className="w-4 h-4" /> Bulk
          </button>
          <button
            type="button"
            onClick={() => {
              setShowAddIqp(true);
              setShowBulkSelect(false);
            }}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" /> Add SS
          </button>
        </div>
      </div>

      {showBulkSelect && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Grade for all selected <span className="text-red-600">*</span>
            </label>
            <select
              value={bulkGrade}
              onChange={(e) => setBulkGrade(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              <option value="">Select grade</option>
              {IQP_GRADE_OPTIONS.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
            {!bulkGrade ? (
              <p className="mt-1 text-xs text-amber-700">Choose a grade to enable Add.</p>
            ) : null}
          </div>
          <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-lg p-2 bg-white mb-3">
            {ssCodesLoading ? (
              <p className="text-center py-4 text-sm text-gray-500">Loading SS codes…</p>
            ) : ssCodesError ? (
              <p className="text-center py-4 text-sm text-red-600">Failed to load SS codes</p>
            ) : (
              groupedSsCodes.map((group) => (
                <div key={`bulk-${group.mainNum}`}>
                  <div className="sticky top-0 bg-gray-100 px-2 py-1.5 text-xs font-semibold text-gray-700">
                    {group.label}
                  </div>
                  {group.codes.map((ss) => {
                    const alreadyHas = iqpList.some(
                      (iqp) => normalizeSsCode(iqp.ss_code) === normalizeSsCode(ss.code)
                    );
                    const isSelected = selectedSsCodes.includes(ss.code);
                    return (
                      <label
                        key={ss.code}
                        className={`flex items-start gap-2 p-2 rounded text-sm ${
                          alreadyHas ? 'opacity-50' : 'hover:bg-gray-50 cursor-pointer'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={alreadyHas}
                          onChange={() => handleBulkSelect(ss.code)}
                          className="mt-0.5"
                        />
                        <span>
                          <strong>{ss.code}</strong> — {ss.name}
                          {alreadyHas && <span className="text-gray-400 ml-1">(registered)</span>}
                        </span>
                      </label>
                    );
                  })}
                </div>
              ))
            )}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowBulkSelect(false);
                setSelectedSsCodes([]);
                setBulkGrade('Registered');
              }}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!selectedSsCodes.length || !bulkGrade || bulkAddMutation.isPending}
              title={
                !bulkGrade
                  ? 'Select a grade first'
                  : !selectedSsCodes.length
                    ? 'Select at least one SS'
                    : undefined
              }
              onClick={() => bulkAddMutation.mutate()}
              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg disabled:opacity-50"
            >
              {bulkAddMutation.isPending ? 'Adding…' : `Add (${selectedSsCodes.length})`}
            </button>
          </div>
        </div>
      )}

      {showAddIqp && !showBulkSelect && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">SS code *</label>
            <select
              value={newIqp.ss_code}
              onChange={(e) => setNewIqp({ ...newIqp, ss_code: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Select…</option>
              {groupedSsCodes.flatMap((g) =>
                g.codes.map((ss) => (
                  <option key={ss.code} value={ss.code}>
                    {ss.code} — {ss.name}
                  </option>
                ))
              )}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Grade *</label>
            <select
              value={newIqp.grade}
              onChange={(e) => setNewIqp({ ...newIqp, grade: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Select…</option>
              {IQP_GRADE_OPTIONS.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Expiry</label>
            <input
              type="date"
              value={newIqp.expiry_date}
              onChange={(e) => setNewIqp({ ...newIqp, expiry_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div className="sm:col-span-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowAddIqp(false);
                setNewIqp({ ss_code: '', grade: '', expiry_date: '' });
              }}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddIqp}
              disabled={addIqpMutation.isPending}
              className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </div>
      )}

      {iqpList.length > 0 ? (
        <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
          {iqpList.map((iqp) => {
            const ssCodeKey = normalizeSsCode(iqp.ss_code);
            const isEditing = editingIqpCode === ssCodeKey;
            const ssCodeDetails = ssCodesData?.find(
              (ss) => normalizeSsCode(ss.code) === ssCodeKey
            );
            const expiryDays =
              iqp.expiry_date != null
                ? Math.ceil(
                    (new Date(iqp.expiry_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
                  )
                : null;

            return (
              <div
                key={iqp.id || `${iqp.worker_id}-${iqp.ss_code}`}
                className={`border rounded-lg p-3 ${
                  isEditing ? 'border-primary-400 bg-primary-50/40' : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">{iqp.ss_code}</span>
                      {!isEditing && (
                        <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                          {iqp.grade}
                        </span>
                      )}
                    </div>
                    {ssCodeDetails && (
                      <p className="text-sm text-gray-600 mt-0.5">{ssCodeDetails.name}</p>
                    )}
                    {!isEditing && iqp.expiry_date && (
                      <p
                        className={`text-xs mt-1 ${
                          expiryDays != null && expiryDays < 0
                            ? 'text-red-600'
                            : expiryDays != null && expiryDays <= 90
                              ? 'text-amber-600'
                              : 'text-gray-500'
                        }`}
                      >
                        Expires {new Date(iqp.expiry_date).toLocaleDateString()}
                        {expiryDays != null && expiryDays < 0
                          ? ' (expired)'
                          : expiryDays != null && expiryDays <= 90
                            ? ` (${expiryDays}d left)`
                            : ''}
                      </p>
                    )}
                  </div>
                  {!isEditing && (
                    <div className="flex gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => startEditIqp(iqp)}
                        className="p-1.5 text-gray-600 hover:text-primary-700 hover:bg-gray-100 rounded"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Remove ${iqp.ss_code}?`)) {
                            deleteIqpMutation.mutate(iqp.ss_code);
                          }
                        }}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                {isEditing && (
                  <div className="mt-3 pt-3 border-t border-primary-200 grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <select
                      value={editIqpDraft.grade}
                      onChange={(e) =>
                        setEditIqpDraft((p) => ({ ...p, grade: e.target.value }))
                      }
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">Grade *</option>
                      {IQP_GRADE_OPTIONS.map((g) => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                      {!IQP_GRADE_OPTIONS.includes(editIqpDraft.grade) && editIqpDraft.grade ? (
                        <option value={editIqpDraft.grade}>{editIqpDraft.grade}</option>
                      ) : null}
                    </select>
                    <input
                      type="date"
                      value={editIqpDraft.expiry_date}
                      onChange={(e) =>
                        setEditIqpDraft((p) => ({ ...p, expiry_date: e.target.value }))
                      }
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => saveEditIqp(iqp.ss_code)}
                        disabled={updateIqpMutation.isPending}
                        className="flex-1 px-3 py-2 text-sm bg-primary-600 text-white rounded-lg disabled:opacity-50"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingIqpCode(null);
                          setEditIqpDraft({ grade: '', expiry_date: '' });
                        }}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6 text-gray-500 border border-dashed border-gray-200 rounded-lg">
          <Award className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No SS codes registered yet</p>
        </div>
      )}
    </div>
  );
}
